import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { Menu, Select, Table } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { EmptyState, InfiniteScroll, SearchInput, Spin } from 'common/components';
import { ERoute } from 'common/const';
import { getFilterOptions } from 'common/helpers';
import {
  DEFAULT_EMPTY_VALUE,
  DEFAULT_PAGINATION_PAGE,
  LIST_LIMIT_20,
  PREVIOUS_PATH_KEY,
  SELECT_LIST_HEIGHT_320,
} from 'common/config';
import { ArrowDownShort } from 'common/svg';
import { UserHooks } from 'common/hooks';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ERequestListPageTab, ERequestStatus } from 'entities/Request/Request.enum';
import { getRequestListPayload, getRequestListTabs, renderRequestListRecords } from 'entities/Request/Request.helper';
import { IRequestListPayload } from 'entities/Request/Request.models';
import { EUserRole } from 'entities/User/User.enum';
import {
  getRequestAddresses,
  getRequestBuyers,
  getRequestList,
  getRequestListChunk,
  getRequestStats,
  getRequestSubdivisions,
  getRequestUsers,
  selectIsRequestLoading,
  selectRequestAddresses,
  selectRequestBuyers,
  selectRequestCount,
  selectRequestList,
  selectRequestStats,
  selectRequestSubdivisions,
  selectRequestUsers,
  setRequestList,
} from 'entities/Request/Request.slice';
import { getUserStats, selectCurrentUser } from 'entities/User/User.slice';

interface IComponentProps {
  header: ReactNode;
  role?: string;
  tab?: string;
}

export const RequestListTab: FC<IComponentProps> = ({ header, role = EUserRole.User, tab }) => {
  const dispatch = useAppDispatch();
  // request
  const requestList = useAppSelector(selectRequestList);
  const requestCount = useAppSelector(selectRequestCount);
  const isRequestLoading = useAppSelector(selectIsRequestLoading);
  const requestBuyers = useAppSelector(selectRequestBuyers);
  const requestSubdivisions = useAppSelector(selectRequestSubdivisions);
  const requestUsers = useAppSelector(selectRequestUsers);
  const requestAddresses = useAppSelector(selectRequestAddresses);
  const requestStats = useAppSelector(selectRequestStats);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [scrollLoading, setScrollLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGINATION_PAGE);
  const [payload, setPayload] = useState<IRequestListPayload>({});
  const location = useLocation();
  const navigate = useNavigate();
  const { isManager } = UserHooks.useCurrentUserRole();

  const asUser = role === EUserRole.User;
  const asManager = role === EUserRole.Manager;
  const asSellerManager = role === EUserRole.SellerManager;
  const buyerOptions = getFilterOptions.buyer(requestBuyers);
  const subdivisionOptions = getFilterOptions.subdivision(requestSubdivisions);
  const userOptions = getFilterOptions.user(requestUsers);
  const addressOptions = getFilterOptions.address(requestAddresses);
  const hasMore = !isRequestLoading && requestList.length < requestCount;
  const tableClassName = useMemo(() => {
    let className = 'table-row-middle';

    if (!!requestList.length && requestList.length === requestCount) {
      className += ' table-end-of-list';
    }

    if (!requestList.length) {
      className += ' table-empty';
    }

    return className;
  }, [requestList, requestCount]);
  const showBuyersFilter = asSellerManager;
  const showSubdivisionsFilter = asManager || payload?.accountId;
  const showAddressFilter = payload?.accountId;

  useEffect(() => {
    const fetch = async () => {
      setFetchLoading(true);
      dispatch(setRequestList([]));
      setPage(DEFAULT_PAGINATION_PAGE);

      const payload = getRequestListPayload(role, tab);

      if (asSellerManager) {
        await dispatch(getRequestBuyers({ statuses: payload.statuses }));
      }

      if (asManager) {
        await dispatch(getRequestSubdivisions({ statuses: payload.statuses }));
        await dispatch(getRequestUsers({ statuses: payload.statuses }));
      }

      setPayload(payload);
      await dispatch(getRequestList(payload));
      setFetchLoading(false);
      await dispatch(getRequestStats({ myRequests: asUser }));
      dispatch(getUserStats());
    };

    fetch();
  }, [role, tab]);

  const onBuyerChange = async (value: number) => {
    const newPayload = {
      ...payload,
      accountId: value === DEFAULT_EMPTY_VALUE ? undefined : value,
      subdivisionId: undefined,
      userId: undefined,
      addressId: undefined,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);

    await dispatch(
      getRequestSubdivisions({
        statuses: newPayload.statuses,
        accountId: newPayload.accountId,
      }),
    );
    await dispatch(
      getRequestUsers({
        statuses: newPayload.statuses,
        accountId: newPayload.accountId,
      }),
    );
    await dispatch(
      getRequestAddresses({
        statuses: newPayload.statuses,
        accountId: newPayload.accountId,
      }),
    );
    dispatch(getRequestList(newPayload));
  };

  const onSubdivisionChange = async (value: number) => {
    const newPayload = {
      ...payload,
      subdivisionId: value === DEFAULT_EMPTY_VALUE ? undefined : value,
      userId: undefined,
      addressId: undefined,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);
    await dispatch(
      getRequestUsers({
        statuses: newPayload.statuses,
        accountId: newPayload.accountId,
        subdivisionId: newPayload.subdivisionId,
      }),
    );

    if (asSellerManager) {
      await dispatch(
        getRequestAddresses({
          statuses: newPayload.statuses,
          accountId: newPayload.accountId,
          subdivisionId: newPayload.subdivisionId,
        }),
      );
    }

    dispatch(getRequestList(newPayload));
  };

  const onUserChange = (value: number) => {
    const newPayload = {
      ...payload,
      userId: value === DEFAULT_EMPTY_VALUE ? undefined : value,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);
    dispatch(getRequestList(newPayload));
  };

  const onAddressChange = (value: number) => {
    const newPayload = {
      ...payload,
      addressId: value === DEFAULT_EMPTY_VALUE ? undefined : value,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    setPayload(newPayload);
    dispatch(getRequestList(newPayload));
  };

  const onSearchClick = async (value?: string) => {
    const newPayload = {
      ...payload,
      search: value,
    };

    setPayload(newPayload);
    setPage(DEFAULT_PAGINATION_PAGE);
    await dispatch(getRequestList(newPayload));
  };

  const navigateToRequest = (id: number) => {
    sessionStorage.setItem(PREVIOUS_PATH_KEY, `${location.pathname}${location.search}`);

    if (isManager && asUser) {
      navigate(`/request/${id}?mode=view`);
    } else {
      navigate(`/request/${id}`);
    }
  };

  return (
    <InfiniteScroll
      loading={scrollLoading}
      hasMore={hasMore}
      loader={<Spin />}
      scrollToTopTrigger={[payload]}
      loadMore={async () => {
        setScrollLoading(true);
        setPage(page + 1);
        await dispatch(
          getRequestListChunk({
            ...payload,
            offset: LIST_LIMIT_20 * page,
          }),
        );
        setScrollLoading(false);
      }}
    >
      {header}

      <Menu
        items={getRequestListTabs(requestStats, role)}
        selectedKeys={[tab ? tab : asSellerManager ? ERequestStatus.Processing : ERequestStatus.Created]}
        onClick={({ key }) => navigate(`${ERoute.RequestList}/${role}/${ERequestListPageTab.Requests}/${key}`)}
        mode="horizontal"
      />

      <div className="request-list__filters mb-7">
        {showBuyersFilter && (
          <Select
            rootClassName="redesign"
            className="request-list__filters-select"
            placeholder="Выберите компанию"
            listHeight={SELECT_LIST_HEIGHT_320}
            virtual={false}
            suffixIcon={<ArrowDownShort />}
            options={buyerOptions}
            value={payload?.accountId ? payload.accountId : DEFAULT_EMPTY_VALUE}
            onChange={onBuyerChange}
          />
        )}

        {showSubdivisionsFilter && (
          <>
            <Select
              rootClassName="redesign"
              className="request-list__filters-select"
              placeholder="Выберите подразделение"
              listHeight={SELECT_LIST_HEIGHT_320}
              virtual={false}
              suffixIcon={<ArrowDownShort />}
              options={subdivisionOptions}
              value={payload?.subdivisionId ? payload.subdivisionId : DEFAULT_EMPTY_VALUE}
              onChange={onSubdivisionChange}
            />

            <Select
              rootClassName="redesign"
              className="request-list__filters-select"
              placeholder="Выберите пользователя"
              listHeight={SELECT_LIST_HEIGHT_320}
              virtual={false}
              suffixIcon={<ArrowDownShort />}
              options={userOptions}
              value={payload?.userId ? payload.userId : DEFAULT_EMPTY_VALUE}
              onChange={onUserChange}
            />
          </>
        )}

        {showAddressFilter && (
          <Select
            rootClassName="redesign"
            className="request-list__filters-select"
            placeholder="Выберите адрес"
            listHeight={SELECT_LIST_HEIGHT_320}
            virtual={false}
            suffixIcon={<ArrowDownShort />}
            options={addressOptions}
            value={payload?.addressId ? payload.addressId : DEFAULT_EMPTY_VALUE}
            onChange={onAddressChange}
          />
        )}

        <SearchInput placeholder="Номер заказа" clearDeps={[role]} onSearch={onSearchClick} />
      </div>

      <Table
        className={tableClassName}
        showHeader={false}
        pagination={false}
        dataSource={requestList.map((request) => ({ ...request, key: request.id }))}
        columns={renderRequestListRecords(role, navigateToRequest, currentUser?.account)}
        loading={isRequestLoading}
        locale={{
          emptyText: fetchLoading ? (
            <Spin />
          ) : (
            <EmptyState
              title="В этом разделе нет заказов"
              description={
                asSellerManager
                  ? 'Они появятся здесь после оформления и согласования на стороне закупщика.'
                  : 'Воспользуйтесь каталогом, чтобы найти и заказать нужные вам товары.'
              }
              btnTitle="Перейти в каталог"
              onClick={asSellerManager ? undefined : () => navigate(ERoute.Catalog)}
            />
          ),
        }}
      />
    </InfiniteScroll>
  );
};
